// Cockpit revamp — top KPI strip.
//
// Six cards in a 6-column grid. Pre-launch (status === 'draft') swaps
// content to a "what'll be there once you launch" preview; post-launch
// surfaces live aggregates from /campaigns/:id/report.
import React from 'react';

import { Card } from '../../../ui';
import type { CampaignReportPayload } from '../../../services/campaignsAPI';
import type { Campaign } from '../../../types/campaign';

interface CampaignKpiStripProps {
  campaign: Campaign;
  isPreLaunch: boolean;
  report: CampaignReportPayload | null;
}

interface KpiTileSpec {
  label: string;
  value: string;
  sub: string;
  tone?: 'accent' | 'ok' | 'info' | 'neutral';
}

function audienceCount(campaign: Campaign): number | null {
  // Without a snapshot count, we can't know exactly. Surface "—" until
  // the report endpoint adds an audience-size field.
  if (campaign.audience_filter && Object.keys(campaign.audience_filter).length > 0) {
    const obj = campaign.audience_filter as Record<string, unknown>;
    const n = obj['count'];
    if (typeof n === 'number') return n;
  }
  return null;
}

function dollars(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}k`;
  return `$${v.toFixed(0)}`;
}

function preLaunchTiles(campaign: Campaign): KpiTileSpec[] {
  const audience = audienceCount(campaign);
  return [
    {
      label: 'Audience',
      value: audience !== null ? audience.toLocaleString() : '—',
      sub: 'will snapshot at launch',
    },
    // LT-539: Sequence tile hidden — sequences are system-managed (LT-527).
    {
      label: 'KPIs',
      value: Array.isArray(campaign.kpis)
        ? `${(campaign.kpis as unknown[]).length}`
        : '0',
      sub: 'targets defined',
    },
    {
      label: 'AI ctx',
      value: campaign.ai_context && Object.keys(campaign.ai_context).length > 0 ? '✓' : '—',
      sub: 'Layer 2 written',
      tone: 'ok',
    },
    {
      label: 'Owner',
      value: campaign.owner_name
        || (campaign.owner_user_id ? campaign.owner_user_id.slice(0, 8) : '—'),
      sub: 'campaign owner',
    },
    {
      label: 'Status',
      value: 'draft',
      sub: 'not yet launched',
    },
  ];
}

function activeTiles(
  campaign: Campaign,
  report: CampaignReportPayload | null,
): KpiTileSpec[] {
  const r = report?.active;
  const callbacks = r?.by_exit_status?.['callback_requested'] ?? 0;
  return [
    {
      label: 'Enrolled',
      value: (r?.total_enrolments ?? 0).toLocaleString(),
      sub: 'snapshot at launch',
    },
    {
      label: 'In flight',
      value: (r?.active_count ?? 0).toLocaleString(),
      sub: 'pending steps',
      tone: 'accent',
    },
    {
      label: 'Qualified',
      value: (r?.qualified_count ?? 0).toLocaleString(),
      sub: r ? `${r.conversion_rate_pct.toFixed(1)}% conv` : '—',
      tone: 'ok',
    },
    {
      label: 'Pipeline',
      value: dollars(r?.pipeline_dollars ?? 0),
      sub: 'opp amounts',
      tone: 'ok',
    },
    {
      label: 'Callbacks pending',
      value: callbacks.toLocaleString(),
      sub: 'callback queue',
      tone: 'info',
    },
    {
      label: 'Status',
      value: campaign.status,
      sub: campaign.status === 'active' ? 'dispatching' : 'see lifecycle',
    },
  ];
}

const TONE_CLASSES: Record<NonNullable<KpiTileSpec['tone']>, string> = {
  accent: 'text-accent-text',
  ok: 'text-ok-text',
  info: 'text-info-text',
  neutral: 'text-slate-100',
};

const CampaignKpiStrip: React.FC<CampaignKpiStripProps> = ({
  campaign,
  isPreLaunch,
  report,
}) => {
  const tiles = isPreLaunch ? preLaunchTiles(campaign) : activeTiles(campaign, report);
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
      {tiles.map((t) => (
        <Card key={t.label} padding="tight">
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
            {t.label}
          </div>
          <div
            className={[
              'font-mono text-xl font-semibold mt-1 mb-0.5 truncate',
              t.tone ? TONE_CLASSES[t.tone] : 'text-slate-100',
            ].join(' ')}
            title={t.value}
          >
            {t.value}
          </div>
          <div className="text-[11px] text-slate-500">{t.sub}</div>
        </Card>
      ))}
    </div>
  );
};

export default CampaignKpiStrip;
